import { ApplicantInfo } from "./ApplicantInfo";
import { InfoRow } from "../../../components/InfoRow/InfoRow";
import { CaseInfoContainer } from "./style";
import { defaultNoDataText, type CaseDetailsProps, type CaseDetailsPropsCommon } from "./types";
import { getAccountNumberCommonProcess, getAccountNumberEsProcess } from ".";

function getApplicationStatus(flow: any): string {
  const lastCallback = flow?.data?.lastCallback?.endpoint || "";
  const businessStatus = flow?.data?.businessStatus || "";
  return businessStatus ? `${lastCallback} (${businessStatus})` : lastCallback;
}

function isAddressMatch(flow: any, applicationInfo: any) {
  const valuesToCheck = ['streetName', 'streetNumber', 'postalCode', 'city', 'province'];
  const applicationAddress: any = applicationInfo?.addressInformation;
  const flowAddress: { [key: string]: string } | undefined = flow?.data?.agentApiApplication?.applicant?.addressInformation;
  if (!applicationAddress || !flowAddress) {
    return false;
  }
  return valuesToCheck.every((key) => applicationAddress[key] === flowAddress[key]);
}

export const CaseDetailsFormConfig = ({ flow, isCommonProcess }: CaseDetailsProps) => {
  const appliedLoanAmount = flow?.data?.offer?.finalLoanAmount || defaultNoDataText;
  const accountNumber = (isCommonProcess ? getAccountNumberCommonProcess(flow) : getAccountNumberEsProcess(flow)) || defaultNoDataText;
  const email = flow?.data?.agentApiApplication?.applicant?.emailAddress || defaultNoDataText;
  const phone = flow?.data?.agentApiApplication?.applicant?.mobileNumber || defaultNoDataText;

  const hasAddressInformation = flow?.data?.agentApiApplication?.applicant?.addressInformation;

  // get the address information for the flow
  const streetName = flow?.data?.agentApiApplication?.applicant?.addressInformation?.streetName;
  const streetNumber = flow?.data?.agentApiApplication?.applicant?.addressInformation?.streetNumber;
  const postalCode = flow?.data?.agentApiApplication?.applicant?.addressInformation?.postalCode;
  const city = flow?.data?.agentApiApplication?.applicant?.addressInformation?.city;
  const province = flow?.data?.agentApiApplication?.applicant?.addressInformation?.province;
  const addressString = hasAddressInformation ? `${streetName} ${streetNumber}, ${postalCode} ${city}, ${province}` : defaultNoDataText;


  return (
    <CaseInfoContainer>
      <InfoRow title="Applied loan amount" value={appliedLoanAmount} hasMatch={false} hasPadding={false} />
      <InfoRow title="Account number" value={accountNumber} hasMatch={false} hasPadding={false} />
      <InfoRow title="Email address" value={email} hasMatch={false} hasPadding={false} />
      <InfoRow title="Phone number" value={phone} hasMatch={false} hasPadding={false} />
      {hasAddressInformation && (<InfoRow title="Address" value={addressString} hasMatch={false} hasPadding={false} />)}
      <div style={{
        padding: "0px 10px"
      }}>
      </div>
    </CaseInfoContainer >
  )
}

export const CaseDetailsEs = ({ flow, showLoan, showMatch, applicationInfo }: CaseDetailsProps) => {
  const appliedLoanAmount = flow?.data?.offer?.finalLoanAmount || defaultNoDataText;
  const accountNumberInfo = getAccountNumberEs(flow, applicationInfo);
  const accountNumber = typeof accountNumberInfo === "string" ? accountNumberInfo : accountNumberInfo?.accountNumber || flow?.data?.agentApiApplication?.bankAccountNumber || defaultNoDataText;
  const email = flow?.data?.agentApiApplication?.applicant?.emailAddress || defaultNoDataText;
  const phone = flow?.data?.agentApiApplication?.applicant?.mobileNumber || defaultNoDataText;

  const hasAddressInformation = flow?.data?.agentApiApplication?.applicant?.addressInformation;

  // get the address information for the flow
  const streetName = flow?.data?.agentApiApplication?.applicant?.addressInformation?.streetName;
  const streetNumber = flow?.data?.agentApiApplication?.applicant?.addressInformation?.streetNumber;
  const postalCode = flow?.data?.agentApiApplication?.applicant?.addressInformation?.postalCode;
  const city = flow?.data?.agentApiApplication?.applicant?.addressInformation?.city;
  const province = flow?.data?.agentApiApplication?.applicant?.addressInformation?.province;
  const addressString = hasAddressInformation ? `${streetName} ${streetNumber}, ${postalCode} ${city}, ${province}` : defaultNoDataText;

  // Match flags
  const addressMatch = showMatch ? isAddressMatch(flow, applicationInfo) : false;
  const accountNumberMatch = showMatch ? accountNumber === applicationInfo?.accountNumber : false;
  const emailMatch = showMatch ? email === applicationInfo?.email : false;
  const phoneNumberMatch = showMatch ? phone === applicationInfo?.phone : false;

  const accountNumberValueText = typeof accountNumberInfo === "string"
    ? accountNumberInfo
    : `${accountNumberInfo?.accountNumber} (${accountNumberInfo?.source})`;

  return (
    <CaseInfoContainer>
      {!showLoan && (<InfoRow title="Application status" value={getApplicationStatus(flow)} hasMatch={false} hasPadding={!showLoan} />)}
      {showLoan && (<InfoRow title="Applied loan amount" value={appliedLoanAmount} hasMatch={false} hasPadding={!showLoan} />)}
      <InfoRow title="Account number" value={accountNumberValueText} hasMatch={accountNumberMatch} hasPadding={!showLoan} />
      <InfoRow title="Email address" value={email} hasMatch={emailMatch} hasPadding={!showLoan} />
      <InfoRow title="Phone number" value={phone} hasMatch={phoneNumberMatch} hasPadding={!showLoan} />
      {hasAddressInformation && (<InfoRow title="Address" value={addressString} hasMatch={addressMatch} hasPadding={!showLoan} />)}
      <div style={{
        padding: "0px 10px"
      }}>
      </div>
    </CaseInfoContainer >
  )
}

const getAllAccountNumbersSpain = (flow: any) => {
  const accountNumbers: Record<string, string> = {};

  const { LeaBankWeb, openBankingPSD2 } = flow?.data?.integrations || {};

  const leaBankAccountNumber = LeaBankWeb?.confirmAccountNumber?.accountNumber;
  if (leaBankAccountNumber) {
    accountNumbers["ConfirmAccountNumber"] = leaBankAccountNumber;
  }

  const { bankAccountNumber, applicant} = flow?.data?.agentApiApplication || {};
  if (bankAccountNumber) {
    accountNumbers["AgentApiApplication"] = bankAccountNumber;
  }

  const openBankingAccounts = openBankingPSD2?.balance || [];
  openBankingAccounts.forEach((account: any, index: number) => {
    if (account.accountId) {
      accountNumbers[`OpenBanking_${index}`] = account.accountId;
    }
  });

  const psd2Accounts = applicant?.psD2Data?.accounts || [];
  psd2Accounts.forEach((account: any, index: number) => {
    if (account.accountId) {
      accountNumbers[`PsD2Data_${index}`] = account.accountId;
    }
  });

  return accountNumbers;
}

const getAllAccountNumbersSweden = (flow: any) => {
  const accountNumbers: Record<string, string> = {};

  const leaBankAccountNumber = flow?.data?.integrations?.LeaBankWeb?.customerInformation?.accountNumber;
  if (leaBankAccountNumber) {
    accountNumbers["CustomerInformation"] = leaBankAccountNumber;
  }

  const tinkAccounts = flow?.data?.integrations?.tink?.applicant?.accounts || [];
  tinkAccounts.forEach((account: any, index: number) => {
    if (account.accountNumber) {
      accountNumbers[`Tink_${index}`] = account.accountNumber;
    }
  });
  
  const bankAccountNumber = flow?.data?.agentApiApplication?.bankAccountNumber;
  if (bankAccountNumber) {
    accountNumbers["AgentApiApplication"] = bankAccountNumber;
  }

  return accountNumbers;
}

const findMatchingAccountNumber = (allAccountNumbers: Record<string, string>, applicationAccountNumber: string): { source: string, accountNumber: string } | null => {
  for (const [source, accountNumber] of Object.entries(allAccountNumbers)) {
    if (accountNumber === applicationAccountNumber) {
      let adjustedSource = source;
      if (source.startsWith("Tink_")) {
        adjustedSource = "Tink";
      }
      if (source.startsWith("PsD2Data_")) {
        adjustedSource = "PsD2Data";
      }
      if (source.startsWith("OpenBanking_")) {
        adjustedSource = "OpenBanking";
      }
      return { source: adjustedSource, accountNumber };
    }
  }
  return null;
}

const getAccountNumberEs = (flow: any, applicationInfo: any): {
  source: string;
  accountNumber: string;
} | string | null => {
  const allAccountNumbers = getAllAccountNumbersSpain(flow);
  const applicationAccountNumber = applicationInfo?.accountNumber;

  if (applicationAccountNumber) {
    const matchingAccount = findMatchingAccountNumber(allAccountNumbers, applicationAccountNumber);
    if (matchingAccount) {
      return matchingAccount;
    }
  }

  // If no match found, return the first available account number
  const firstAccountNumber = Object.values(allAccountNumbers)[0];
  return firstAccountNumber || defaultNoDataText;
}

const getAccountNumberCommon = (flow: any, applicationInfo: any): {
  source: string;
  accountNumber: string;
} | string | null => {
  const allAccountNumbers = getAllAccountNumbersSweden(flow);
  const applicationAccountNumber = applicationInfo?.accountNumber;

  if (applicationAccountNumber) {
    const matchingAccount = findMatchingAccountNumber(allAccountNumbers, applicationAccountNumber);
    if (matchingAccount) {
      return matchingAccount;
    }
  }

  // If no match found, return the first available account number
  const firstAccountNumber = Object.values(allAccountNumbers)[0];
  return firstAccountNumber || defaultNoDataText;
};

export const CaseDetailsCommon = ({ flow, showMatch, applicationInfo, coApplicantInfo }: CaseDetailsPropsCommon) => {
  const appliedLoanAmount = flow?.data?.offer?.finalLoanAmount || defaultNoDataText;
  const accountNumberInfo = getAccountNumberCommon(flow, applicationInfo);
  const accountNumber = typeof accountNumberInfo === "string" ? accountNumberInfo : accountNumberInfo?.accountNumber || flow?.data?.agentApiApplication?.bankAccountNumber || defaultNoDataText;
  const accountNumberMatch = showMatch ? accountNumber === applicationInfo?.accountNumber : false;
  const hasCoApplicant = !!flow?.data?.agentApiApplication?.coapplicant;

  const emails = [];
  const phones = [];

  emails.push(applicationInfo.email);
  if (coApplicantInfo) {
    emails.push(coApplicantInfo?.emailAddress);
  }
  phones.push(applicationInfo.phone);
  if (coApplicantInfo) {
    phones.push(coApplicantInfo?.mobileNumber);
  }

  const info = {
    email: emails,
    phone: phones,
  }

  const accountNumberValueText = typeof accountNumberInfo === "string"
    ? accountNumberInfo
    : `${accountNumberInfo?.accountNumber} (${accountNumberInfo?.source})`;

  return (
    <CaseInfoContainer>
      <InfoRow title="Application status" value={getApplicationStatus(flow)} hasMatch={false} hasPadding />
      {hasCoApplicant && (<InfoRow title="Applied loan amount" value={appliedLoanAmount} hasMatch={false} hasPadding />)}
      <InfoRow title="Account number" value={accountNumberValueText} hasMatch={accountNumberMatch} hasPadding />
      <ApplicantInfo flow={flow} info={info} isMainApplicant hasCoApplicant={hasCoApplicant} />
      {hasCoApplicant && (<ApplicantInfo flow={flow} info={info} isMainApplicant={false} hasCoApplicant={hasCoApplicant} />)}
      <div style={{
        padding: "0px 10px"
      }}>
      </div>
    </CaseInfoContainer >
  )
}

